/**
 * Organizational Readiness — the Execution Risk dashboard (DC-1646).
 *
 * The ONE canonical home for the org-scoped execution-risk metric (design:
 * docs/design/v2.0-readiness-dashboard-consolidation.md). Routed at
 * /readiness/organizational under the Readiness destination; the old
 * Settings → Governance ▸ Dashboard home redirects here. Combined Execution
 * Risk factor and the three-pillar breakdown:
 * - People Readiness (40%) - from uploaded interviews
 * - Governance Maturity (35%) - from uploaded documents
 * - Interoperability Maturity (25%) - from enabled standards
 *
 * v2.0: re-skinned onto the cream/teal token scale (lint:cream-guarded);
 * lucide icons replace the emoji set; carries the scope-eyebrow signature
 * (ORGANIZATIONAL READINESS) per the two-lenses design.
 *
 * @version 3.0.0
 * @date 2026-06-11
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardList, Link2 } from 'lucide-react';
import './governance-layout.css';
import './governance-dashboard.css';
import ComplianceGapPanel from '../../components/ComplianceGapPanel';

// Execution Risk from API - supports both flat and nested formats
interface ExecutionRisk {
  // Flat format (preferred)
  people_readiness_score?: number | null;
  governance_maturity_score?: number | null;
  interop_maturity_score?: number | null;
  // Nested format (fallback)
  people_readiness?: { factor: number };
  governance_maturity?: { factor: number };
  interoperability_maturity?: { factor: number };
  // Common fields
  combined_factor: number | null;
  risk_level: string;
  recommendation: string;
  calculated_at: string | null;
}

// Component metrics from API
interface ComponentMetrics {
  interviews: {
    total: number;
    analyzed: number;
    last_updated: string | null;
  };
  documents: {
    total: number;
    analyzed: number;
    gaps: string[];
    last_updated: string | null;
  };
  standards: {
    total: number;
    enabled: number;
    last_validated: string | null;
  };
}

// Recommendations summary from API
interface RecommendationsSummary {
  active_count: number;
  addressed_count: number;
  by_priority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_source: {
    interview: number;
    document: number;
    standard: number;
  };
  last_generated: string | null;
}

// Circular Gauge Component
const CircularGauge: React.FC<{
  value: number;
  riskLevel: string;
  size?: number;
}> = ({ value, riskLevel, size = 140 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(value, 0), 100);
  const dashArray = (progress / 100) * circumference;

  const getLevelClass = (level: string): string => {
    const classes: Record<string, string> = {
      LOW: 'low',
      MODERATE: 'moderate',
      HIGH: 'high',
      CRITICAL: 'critical',
      NOT_CONFIGURED: 'pending',
    };
    return classes[level] || 'pending';
  };

  return (
    <div className="risk-gauge" style={{ width: size, height: size }}>
      <svg className="risk-gauge__svg" viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          className="risk-gauge__bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        {/* Progress circle */}
        <circle
          className={`risk-gauge__fill risk-gauge__fill--${getLevelClass(riskLevel)}`}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={`${dashArray} ${circumference}`}
        />
      </svg>
      <div className="risk-gauge__text">
        <span className="risk-score-card__value">
          {value > 0 ? `${Math.round(value)}%` : '--'}
        </span>
        <span className="risk-score-card__label">Readiness</span>
      </div>
    </div>
  );
};

export const GovernanceDashboard: React.FC = () => {
  const [executionRisk, setExecutionRisk] = useState<ExecutionRisk | null>(null);
  const [metrics, setMetrics] = useState<ComponentMetrics | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch execution risk and recommendations in parallel
      const [riskResponse, recsResponse] = await Promise.all([
        fetch('/api/organization/execution-risk'),
        fetch('/api/organization/recommendations'),
      ]);

      if (!riskResponse.ok) {
        throw new Error(`Failed to load execution risk: ${riskResponse.statusText}`);
      }

      const riskData = await riskResponse.json();
      if (riskData.success) {
        setExecutionRisk(riskData.execution_risk || null);
        setMetrics(riskData.metrics || null);
      } else {
        throw new Error(riskData.error || 'Failed to load dashboard data');
      }

      // Load recommendations summary (don't fail if this errors)
      if (recsResponse.ok) {
        const recsData = await recsResponse.json();
        if (recsData.success) {
          setRecommendations(recsData.summary || null);
        }
      }
    } catch (err: unknown) {
      console.error('[GovernanceDashboard] Load error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Get risk level class
  const getRiskLevelClass = (level: string): string => {
    const classes: Record<string, string> = {
      LOW: 'low',
      MODERATE: 'moderate',
      HIGH: 'high',
      CRITICAL: 'critical',
      NOT_CONFIGURED: 'pending',
    };
    return classes[level] || 'pending';
  };

  // Get risk level label
  const getRiskLevelLabel = (level: string): string => {
    const labels: Record<string, string> = {
      LOW: 'Low Risk',
      MODERATE: 'Moderate Risk',
      HIGH: 'High Risk',
      CRITICAL: 'Critical Risk',
      NOT_CONFIGURED: 'Not Configured',
    };
    return labels[level] || level;
  };

  // Get pillar status class
  const getPillarStatusClass = (score: number | null): string => {
    if (score === null) return 'pillar--pending';
    if (score >= 75) return 'pillar--good';
    if (score >= 50) return 'pillar--fair';
    return 'pillar--low';
  };

  // Get pillar icon (lucide — the emoji set retired with the v2.0 re-skin)
  const getPillarIcon = (pillar: 'people' | 'governance' | 'interop'): React.ReactNode => {
    const icons: Record<string, React.ReactNode> = {
      people: <Users size={18} aria-hidden="true" />,
      governance: <ClipboardList size={18} aria-hidden="true" />,
      interop: <Link2 size={18} aria-hidden="true" />,
    };
    return icons[pillar];
  };

  // Helper to extract score from either flat or nested format
  const getPeopleScore = (): number | null => {
    if (!executionRisk) return null;
    // Try flat format first
    if (typeof executionRisk.people_readiness_score === 'number') {
      return executionRisk.people_readiness_score;
    }
    // Fall back to nested format (factor is 0-1, convert to 0-100)
    if (executionRisk.people_readiness?.factor != null) {
      return executionRisk.people_readiness.factor * 100;
    }
    return null;
  };

  const getGovernanceScore = (): number | null => {
    if (!executionRisk) return null;
    if (typeof executionRisk.governance_maturity_score === 'number') {
      return executionRisk.governance_maturity_score;
    }
    if (executionRisk.governance_maturity?.factor != null) {
      return executionRisk.governance_maturity.factor * 100;
    }
    return null;
  };

  const getInteropScore = (): number | null => {
    if (!executionRisk) return null;
    if (typeof executionRisk.interop_maturity_score === 'number') {
      return executionRisk.interop_maturity_score;
    }
    if (executionRisk.interoperability_maturity?.factor != null) {
      return executionRisk.interoperability_maturity.factor * 100;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="governance-loading">
        <div className="spinner" />
        <p>Loading execution risk dashboard...</p>
      </div>
    );
  }

  const riskLevel = executionRisk?.risk_level || 'NOT_CONFIGURED';
  const riskValue = executionRisk?.combined_factor
    ? executionRisk.combined_factor * 100
    : 0;

  return (
    <div className="governance-section governance-dashboard">
      {/* Scope-eyebrow header (DC-1646 — every readiness screen labels its
          axis and scope) */}
      <div className="governance-section__header">
        <div className="governance-section__title">
          <span className="gov-dash-eyebrow">Organizational readiness · execution axis</span>
          <h2>Execution Risk</h2>
          <p className="governance-section__subtitle">
            Can the organization execute? People, governance, and interoperability readiness for
            FAIR data initiatives — org-scoped, one home.
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="governance-error">
          <span className="governance-error__icon">!</span>
          <span className="governance-error__message">{error}</span>
          <button onClick={() => setError(null)} className="governance-error__dismiss">
            X
          </button>
        </div>
      )}

      {/* Main Risk Score - Hero Card */}
      <div className="risk-overview">
        <div className="risk-score-card">
          <div className="risk-score-card__body">
            {/* Circular Gauge */}
            <CircularGauge value={riskValue} riskLevel={riskLevel} />

            {/* Risk Details */}
            <div className="risk-details">
              <div className={`risk-score-card__level risk-score-card__level--${getRiskLevelClass(riskLevel)}`}>
                {getRiskLevelLabel(riskLevel)}
              </div>
              {executionRisk?.recommendation && (
                <p className="risk-score-card__recommendation">
                  {executionRisk.recommendation}
                </p>
              )}
              {executionRisk?.calculated_at && (
                <span className="risk-score-card__timestamp">
                  Updated: {new Date(executionRisk.calculated_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Three Pillars */}
      <div className="pillars-grid">
        {/* People Readiness */}
        <div className="pillar-card">
          <div className="pillar-card__header">
            <div className={`pillar-card__icon ${getPillarStatusClass(getPeopleScore())}`}>
              {getPillarIcon('people')}
            </div>
            <div className="pillar-card__info">
              <h4>People Readiness</h4>
              <span className="pillar-card__weight">40% weight</span>
            </div>
          </div>
          <div className="pillar-card__score">
            <div className="pillar-meter">
              <div
                className="pillar-meter__fill"
                style={{ width: `${getPeopleScore() ?? 0}%` }}
              />
            </div>
            <span className="pillar-card__value">
              {getPeopleScore() != null
                ? `${Math.round(getPeopleScore()!)}%`
                : '--'}
            </span>
          </div>
          <div className="pillar-card__metrics">
            <div className="metric">
              <span className="metric__value">{metrics?.interviews.total ?? 0}</span>
              <span className="metric__label">Interviews</span>
            </div>
            <div className="metric">
              <span className="metric__value">{metrics?.interviews.analyzed ?? 0}</span>
              <span className="metric__label">Analyzed</span>
            </div>
          </div>
          <Link to="/settings/team/interviews" className="pillar-card__link">
            Manage Interviews
          </Link>
        </div>

        {/* Governance Maturity */}
        <div className="pillar-card">
          <div className="pillar-card__header">
            <div className={`pillar-card__icon ${getPillarStatusClass(getGovernanceScore())}`}>
              {getPillarIcon('governance')}
            </div>
            <div className="pillar-card__info">
              <h4>Governance Maturity</h4>
              <span className="pillar-card__weight">35% weight</span>
            </div>
          </div>
          <div className="pillar-card__score">
            <div className="pillar-meter">
              <div
                className="pillar-meter__fill"
                style={{ width: `${getGovernanceScore() ?? 0}%` }}
              />
            </div>
            <span className="pillar-card__value">
              {getGovernanceScore() != null
                ? `${Math.round(getGovernanceScore()!)}%`
                : '--'}
            </span>
          </div>
          <div className="pillar-card__metrics">
            <div className="metric">
              <span className="metric__value">{metrics?.documents.total ?? 0}</span>
              <span className="metric__label">Documents</span>
            </div>
            <div className="metric">
              <span className="metric__value">{metrics?.documents.gaps?.length ?? 0}</span>
              <span className="metric__label">Gaps</span>
            </div>
          </div>
          <Link to="/settings/governance/documents" className="pillar-card__link">
            Manage Documents
          </Link>
        </div>

        {/* Interoperability Maturity */}
        <div className="pillar-card">
          <div className="pillar-card__header">
            <div className={`pillar-card__icon ${getPillarStatusClass(getInteropScore())}`}>
              {getPillarIcon('interop')}
            </div>
            <div className="pillar-card__info">
              <h4>Interoperability</h4>
              <span className="pillar-card__weight">25% weight</span>
            </div>
          </div>
          <div className="pillar-card__score">
            <div className="pillar-meter">
              <div
                className="pillar-meter__fill"
                style={{ width: `${getInteropScore() ?? 0}%` }}
              />
            </div>
            <span className="pillar-card__value">
              {getInteropScore() != null
                ? `${Math.round(getInteropScore()!)}%`
                : '--'}
            </span>
          </div>
          <div className="pillar-card__metrics">
            <div className="metric">
              <span className="metric__value">{metrics?.standards.total ?? 0}</span>
              <span className="metric__label">Standards</span>
            </div>
            <div className="metric">
              <span className="metric__value">{metrics?.standards.enabled ?? 0}</span>
              <span className="metric__label">Enabled</span>
            </div>
          </div>
          <Link to="/settings/governance/standards" className="pillar-card__link">
            Manage Standards
          </Link>
        </div>
      </div>

      {/* Compliance Gap Panel - Policy vs Practice */}
      <ComplianceGapPanel />

      {/* Recommendations Summary Card */}
      {recommendations && recommendations.active_count > 0 && (
        <div className="recommendations-summary-card">
          <div className="recommendations-summary-card__header">
            <div className="recommendations-summary-card__icon">
              <ClipboardList size={18} aria-hidden="true" />
            </div>
            <div className="recommendations-summary-card__title">
              <h4>Organizational Recommendations</h4>
              <span className="recommendations-summary-card__subtitle">
                Actions from interviews &amp; documents
              </span>
            </div>
          </div>
          <div className="recommendations-summary-card__stats">
            <div className="rec-stat rec-stat--total">
              <span className="rec-stat__value">{recommendations.active_count}</span>
              <span className="rec-stat__label">Active</span>
            </div>
            {recommendations.by_priority.critical > 0 && (
              <div className="rec-stat rec-stat--critical">
                <span className="rec-stat__value">{recommendations.by_priority.critical}</span>
                <span className="rec-stat__label">Critical</span>
              </div>
            )}
            {recommendations.by_priority.high > 0 && (
              <div className="rec-stat rec-stat--high">
                <span className="rec-stat__value">{recommendations.by_priority.high}</span>
                <span className="rec-stat__label">High</span>
              </div>
            )}
            {recommendations.by_priority.medium > 0 && (
              <div className="rec-stat rec-stat--medium">
                <span className="rec-stat__value">{recommendations.by_priority.medium}</span>
                <span className="rec-stat__label">Medium</span>
              </div>
            )}
          </div>
          <div className="recommendations-summary-card__sources">
            {recommendations.by_source.document > 0 && (
              <span className="source-badge source-badge--document">
                {recommendations.by_source.document} from documents
              </span>
            )}
            {recommendations.by_source.interview > 0 && (
              <span className="source-badge source-badge--interview">
                {recommendations.by_source.interview} from interviews
              </span>
            )}
            {recommendations.by_source.standard > 0 && (
              <span className="source-badge source-badge--standard">
                {recommendations.by_source.standard} from standards
              </span>
            )}
          </div>
          <Link to="/settings/governance/recommendations" className="recommendations-summary-card__link">
            View All Recommendations →
          </Link>
        </div>
      )}

      {/* Help Section */}
      <div className="governance-help">
        <h4>About Execution Risk</h4>
        <p>
          Execution Risk measures your organization's readiness to successfully implement
          FAIR data initiatives. It combines three organizational assessments:
        </p>
        <ul>
          <li>
            <strong>People Readiness (40%)</strong> - Stakeholder sentiment from uploaded
            interview transcripts
          </li>
          <li>
            <strong>Governance Maturity (35%)</strong> - Policy completeness from uploaded
            governance documents
          </li>
          <li>
            <strong>Interoperability Maturity (25%)</strong> - Compliance with enabled
            data exchange standards
          </li>
        </ul>
        <p>
          A higher readiness score indicates lower execution risk. Upload interviews, documents,
          and configure standards to improve your organization's readiness profile.
        </p>
      </div>
    </div>
  );
};

export default GovernanceDashboard;
